import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import CarrierStatus from 'components/marketplace/CarrierStatus'
import { FeedbackMessage } from 'components/marketplace/FeedbackMessage'
import SafetyStatus from 'components/marketplace/SafetyStatus'
import { Card } from 'transfix-ui/components/Card'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { getCarrierInfo } from 'apps/booking/utils/actions/carrier'
import { selectCompliance, selectCarrierProfileInfo } from 'apps/booking/utils/selectors/carrier'
import { selectCoverLoadSuccess } from 'apps/booking/utils/selectors/coverLoad'
import { selectShipmentCoveredBid } from 'apps/booking/utils/selectors/shipment'
import { activeRowIdSelector } from 'apps/booking/utils/selectors/sidebar'
import { InfoIcon, PreferredIcon, TrackingIcon, WarningFilledIcon } from 'transfix-icons'
import { DISPLAY } from 'constants/display'

const CarrierProfileCard = () => {
  const dispatch = useDispatch()
  const {
    dotNumber,
    fmcsaRating,
    id: carrierId,
    isEldApproved,
    isTrusted,
    legalName,
    mcNumber,
    state: carrierStatus,
  } = useSelector(selectCarrierProfileInfo)
  const compliance = useSelector(selectCompliance)
  const coveredBid = useSelector(selectShipmentCoveredBid)
  const isCoverLoadSuccess = useSelector(selectCoverLoadSuccess)
  const { activeRowId: shipmentId } = useSelector(activeRowIdSelector)

  useEffect(() => {
    if (!isCoverLoadSuccess && coveredBid) {
      dispatch(getCarrierInfo(coveredBid.carrierApplicationId, shipmentId))
    }
  }, [dispatch, shipmentId, coveredBid, isCoverLoadSuccess])

  const trackingClasses = classnames('flex', 'items-center', 'mt1', 'tracking-approval-check', {
    'tracking-info': isEldApproved,
  })
  return (
    <Card className='mb4'>
      <strong>Carrier</strong>
      <div className='mt4'>
        {legalName ? (
          <>
            <a href={`${__ROOT__}/carriers/${carrierId}`} target='_blank' rel='noopener noreferrer'>
              {legalName}
            </a>
            {isTrusted && <PreferredIcon className='inline-right' />}
          </>
        ) : (
          <p className='text-secondary'>Carrier Info will appear when a carrier is selected</p>
        )}
      </div>
      <strong>MC:</strong> {mcNumber || DISPLAY.na}
      <span className='mh1'>&nbsp;&#183;&nbsp;</span>
      <strong>DOT:</strong> {dotNumber || DISPLAY.na}
      {carrierStatus && (
        <div className='mt4'>
          <CarrierStatus displayStatus status={carrierStatus} />
          <span className='mr4' />
          {fmcsaRating && <SafetyStatus status={fmcsaRating} />}
        </div>
      )}
      {carrierId && (
        <div className={trackingClasses}>
          <div className='mt1'>
            <TrackingIcon />
          </div>
          <div className='ml1'>P44 Tracking {!isEldApproved ? 'Not' : ''} Available</div>
          <Tooltip
            position='bottom'
            type='dark'
            trigger={
              <div data-test='tooltip-icon' className='mt1'>
                <InfoIcon color='coolGray5' className='ml1' size='medium' />
              </div>
            }>
            <div className='tracking-tooltip-text'>
              {isEldApproved
                ? 'A valid truck number must be added to the shipment in order to track via project44. Check shipment details before dispatch to ensure that a valid truck number has been approved.'
                : 'This carrier has not been onboarded and approved to track via project44'}
            </div>
          </Tooltip>
        </div>
      )}
      <section>
        {compliance?.loading && (
          <div className='flex flex-column items-center'>
            <div className='mt4'>
              <Spinner position='relative' />
            </div>
            <p className='i mv2'>Checking compliance requirements ...</p>
          </div>
        )}
        {compliance?.failed && (
          <FeedbackMessage
            message='Failed to check compliance. Please try again later.'
            type='error'
          />
        )}
        {Boolean(compliance?.errors?.length) && (
          <>
            <hr />
            <div className='flex items-center'>
              <WarningFilledIcon color='warning' className='mr2' size='large' />
              <p className='b text-warning'>Does not meet compliance requirements</p>
            </div>
            <div className='coolGray1 pa4 mt1'>
              <ul className='list-unstyled'>
                {compliance?.errors?.map(error => (
                  <li key={error} data-test='compliance-error-text'>
                    <p className='pt0'>{error}</p>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </section>
    </Card>
  )
}
export default CarrierProfileCard
